import {useCallback, useEffect, useMemo, useState} from "react";
import {useDebounce} from "./use-debounce.js";
export function useTableState(dataSource, tableLimits, search) {
  const initialLimit = tableLimits[0];
  const [pagination, setPagination] = useState(1);
  const [state, setState] = useState({
    limit: initialLimit,
    ordering: {},
    page: 1,
    selectedRows: [],
    term: search
  });
  const allIds = useMemo(() => dataSource.map((data) => data.id), [dataSource]);
  const debouncedPagination = useDebounce(pagination, 500);
  const onPageNumberChange = useCallback(() => {
    setState((prevState) => ({
      ...prevState,
      page: debouncedPagination
    }));
  }, [debouncedPagination]);
  const onPaginationChange = useCallback((page) => {
    setPagination(page);
    setState((prevState) => ({
      ...prevState,
      selectedRows: []
    }));
  }, []);
  const onLimitChange = useCallback((limit) => {
    setState((prevState) => ({
      ...prevState,
      limit,
      page: 1
    }));
  }, []);
  const onOrderingChange = useCallback((field) => {
    let ordering = {};
    const orderFields = field.split(",");
    const mainOrder = orderFields.shift();
    if (!(mainOrder in state.ordering)) {
      ordering = {
        [mainOrder]: "ASC"
      };
      orderFields.forEach((orderKey) => {
        Object.assign(ordering, {
          [orderKey]: "ASC"
        });
      });
    }
    if (mainOrder in state.ordering && state.ordering[`${mainOrder}`] === "ASC") {
      ordering = {
        [mainOrder]: "DESC"
      };
      orderFields.forEach((orderKey) => {
        Object.assign(ordering, {
          [orderKey]: "ASC"
        });
      });
    }
    setState((prevState) => ({
      ...prevState,
      ordering
    }));
  }, [state.ordering]);
  const onSearch = useCallback((term) => {
    onPaginationChange(1);
    setState((prevState) => ({
      ...prevState,
      page: 1,
      term,
      selectedRows: []
    }));
  }, [onPaginationChange]);
  const onRowSelect = useCallback((rowId) => {
    setState((prevState) => {
      const index = prevState.selectedRows.indexOf(rowId);
      if (index === -1) {
        return {
          ...prevState,
          selectedRows: [...prevState.selectedRows, rowId]
        };
      }
      return {
        ...prevState,
        selectedRows: [...prevState.selectedRows.slice(0, index), ...prevState.selectedRows.slice(index + 1)]
      };
    });
  }, []);
  const onRowSelectAll = useCallback(() => {
    const areAlreadySelected = state.selectedRows.length === dataSource.length;
    setState((prevState) => ({
      ...prevState,
      selectedRows: areAlreadySelected ? [] : allIds
    }));
  }, [allIds, dataSource.length, state.selectedRows.length]);
  const deselectAll = useCallback(() => {
    setState((prevState) => ({
      ...prevState,
      selectedRows: []
    }));
  }, []);
  useEffect(() => {
    onPageNumberChange();
  }, [pagination, onPageNumberChange]);
  return {
    ...state,
    pagination,
    deselectAll,
    onLimitChange,
    onOrderingChange,
    onPaginationChange,
    onRowSelect,
    onRowSelectAll,
    onSearch
  };
}
